import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../Store";
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Button,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useSnackbar } from "../shared/SnackbarProvider";
import { Repo } from "../Repo";

const ObservationsPage: React.FC = () => {
  const { observationData } = useContext(AppContext);
  const [showAll, setShowAll] = useState(false);
  const [filteredObservations, setSfilteredObservations] = useState([]);

  const snack = useSnackbar();
  const repo = new Repo();

  useEffect(() => {
    if (!observationData) return;
    const filtered = showAll
      ? observationData
      : observationData?.filter((obs: any) => !obs.reviewed);

    setSfilteredObservations(filtered);

  }, [showAll, observationData]);

  const markReviewed = async (observation: any) => {
    try {
      await repo.markObservationReviewed(observation);
      snack("'Reviewed' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as reviewed.", "error");
    }

  };

  const markVisibleToParents = async (observation: any) => {
    try {
      await repo.markObservationVisibleToParents(observation);
      snack("'Visible to Parents' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as reviewed.", "error");
    }
  };

  const deleteObservation = async (observation: any) => {

    try {
      await repo.deleteObservation(observation);
      snack("'Deleted' status changed", "success");
    }
    catch (e) {
      snack("Error marking observation as deleted.", "error");
    }
  };


  const resaveAllObservationReviews = async () => {
    try {
      await repo.resaveAllObservationReviews(filteredObservations);
      snack("Resaved", "success");
    }
    catch (e) {
      snack("Error resaving.", "error");
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        Observations
      </Typography>

      <Button
        variant="contained"
        onClick={() => setShowAll(!showAll)}
      >
        {showAll ? "Show Only Unreviewed" : "Show All"}
      </Button>

      <Button
        variant="contained"
        onClick={() => resaveAllObservationReviews()}
      >
        Resave All
      </Button>

      <List>
        {filteredObservations
          .sort((a: any, b: any) => a.childId.localeCompare(b.childId))
          .map((obs: any) => (
            <ListItem
              key={obs.id}
              id={obs.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <ListItemAvatar>
                  <Avatar
                    src={obs.photoUrl}
                    alt={obs.category}
                    sx={{ width: 128, height: 128, borderRadius: 5, marginRight: "10px" }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    obs.childId.split(",").map((x: string) => x.trim()).map((childId: string) => {
                      return (
                        <Link to={`/child/${childId}`} style={{ textDecoration: "none" }}>
                          <Chip
                            label={childId}
                            avatar={<Avatar>{childId.charAt(0).toUpperCase()}</Avatar>}
                            clickable
                            component="span"
                          />
                        </Link>
                      )
                    })
                  }
                  secondary={
                    <>
                      <Typography color="text.secondary">
                        <b>Category</b>: {obs.category}
                      </Typography>
                      <Typography color="text.secondary">
                        <b>Date</b>: {format(new Date(obs.observationDate), "yyyy-MMM-dd")}
                      </Typography>
                      <Typography color="text.secondary">
                        <b>Familiarity</b>: {obs.familiarity}
                      </Typography>
                      <Typography color="text.secondary">
                        <b>Concentration</b>: {obs.concentration}
                      </Typography>
                      <Typography color="text.secondary">
                        <b>Notes</b>: {obs.observationNotes}
                      </Typography>
                      <Typography color="text.secondary">
                        <b>Next steps</b>: {obs.nextSteps}
                      </Typography>
                    </>
                  }
                />
                <Box display="flex" flexDirection="column" gap={1} ml="auto">
                  <Button
                    variant="outlined"
                    endIcon={<CheckCircleIcon />}
                    color={obs.reviewed ? "success" : "primary"}
                    onClick={() => markReviewed(obs)}
                    title="Mark as Reviewed"
                    fullWidth
                  >
                    Reviewed
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<VisibilityIcon />}
                    color={obs.visibleToParents ? "success" : "primary"}
                    onClick={() => markVisibleToParents(obs)}
                    title="Mark as Visible to Parents"
                    fullWidth
                  >
                    Visible
                  </Button>
                  <Button
                    variant="outlined"
                    component={Link}
                    to={obs.editUrl}
                    target="_blank"
                    endIcon={<EditIcon />}
                    title="Mark as Visible to Parents"
                    fullWidth
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    endIcon={<DeleteIcon />}
                    color="warning"
                    onClick={() => deleteObservation(obs)}
                    title="Delete observation"
                    fullWidth
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </ListItem>
          ))}
      </List>
    </Box>
  );
};

export default ObservationsPage;
